import { HttpStatusCode, ErrorMessage } from 'constants/globals'
import { setCurrentUser, getCurrentUser } from './user'
import { logoutUserInternal } from './helpers'
import { isInImmediateAuthWindow } from './authReady'
import { getApiWorkspaceUrl, getApiWorkspaceUrlV2 } from './endpoints'
import { Action, State, TypesArray } from './types'
import { listHabilityCacheUrl } from 'apis/listCache'
import FetchController from './fetchController'
import { TokenResponse, User } from 'constants/types'
import Token from './token'

const PENDING_REQUESTS_DELAY = 100

const loginApp = 'hub-app'

/** True mientras se está renovando la sesión (refresh); `fetchNext` espera para no enviar el access anterior. */
let sessionRefreshInProgress = false

/** Una sola promesa de refresh en vuelo: 401 concurrentes y `refreshTokenUserAsync` comparten el mismo intercambio. */
let refreshSessionPromise: Promise<void> | null = null

const ApiUrl = getApiWorkspaceUrl()
const ApiUrlV2 = getApiWorkspaceUrlV2()

const intervals: any[] = []

function checkStatus(response: Response) {
  if (response.status >= HttpStatusCode.OK && response.status < HttpStatusCode.MultipleChoices) {
    return response
  }

  const error: Error & { response?: Response; status?: number } = new Error(response.statusText)

  error.response = response
  error.status = response.status
  throw error
}

// noinspection JSUnusedGlobalSymbols
export function genericResponseHandler(state: State | any, action: Action, typesArray: TypesArray[]): State {
  if (state.execCall === true) {
    let upState: any = {}
    typesArray.forEach((typeFromArr: Record<string, string>) => {
      const o: any = {}
      if (action.type === typeFromArr.successType) {
        o[typeFromArr.stateObj] = action[typeFromArr.stateObj]
        upState = { ...o }
      }
    })

    state.assign(...state, ...upState)
  }
  return state
}

function parseJSON(response: Response, options?: any, typeRequest?: string): Promise<any> {
  if (!response) return Promise.resolve({})
  return response.text().then((text) => {
    const saveCache = listHabilityCacheUrl.find((item) => item.type === typeRequest)

    if (saveCache && options?.method === 'GET' && response.status === 200) {
      setCacheResponse(response.url, text ? JSON.parse(text) : {}, typeRequest)
    }

    return text ? JSON.parse(text) : {}
  })
}

/**
 * I removed api logout api call since at this point access and refresh token are invalid, so request will fail
 * It is enough to remove user from local storage and redirect to login page.
 */

const requestsCounter = { count: 0 }

/**
 * Intercambia el refresh por nuevos tokens. Usa `fetch` directo (no `request`) para que un 401
 * en este endpoint no dispare otro refresh en cadena.
 */
async function exchangeRefreshToken(refreshToken: string): Promise<TokenResponse> {
  const fullUrl = `${String(ApiUrl)}/auth/refreshToken`
  const res = await fetch(fullUrl, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${refreshToken}`
    }
  })
  if (!res.ok) {
    await res.text().catch(() => {})
    throw new Error('Refresh token exchange failed')
  }
  return res.json() as Promise<TokenResponse>
}

function applySessionFromRefreshResponse(previousUser: User, response: TokenResponse): void {
  const accessToken = response.access_token
  const newUser: User = {
    ...previousUser,
    token: accessToken,
    accessToken,
    refreshToken: response.refresh_token ?? previousUser.refreshToken,
    expRefresh: response.refresh_expires_in ?? previousUser.expRefresh,
    tickerVisible: previousUser.tickerVisible
  }
  setCurrentUser(newUser, new Token().setToken(accessToken, newUser.refreshToken))
}

/**
 * Renueva access (+ refresh) una sola vez aunque haya varios 401 o llamadas concurrentes.
 */
function runRefreshSession(): Promise<void> {
  if (refreshSessionPromise) return refreshSessionPromise

  sessionRefreshInProgress = true
  refreshSessionPromise = (async () => {
    try {
      const user = getCurrentUser()
      if (!user?.refreshToken) {
        throw new Error('No refresh token')
      }
      const response = await exchangeRefreshToken(user.refreshToken)
      applySessionFromRefreshResponse(user, response)
    } catch {
      if (intervals.length) {
        intervals.forEach((interval) => clearInterval(interval))
      }
      logoutUserInternal()
      throw new Error('refresh failed')
    } finally {
      sessionRefreshInProgress = false
      refreshSessionPromise = null
    }
  })()

  return refreshSessionPromise
}

function request(url: string, options: Object, typeRequest?: string): Promise<any> {
  const user = getCurrentUser()
  return fetch(url, options)
    .then(checkStatus)
    .then((a) => parseJSON(a, options, typeRequest))
    .then((data) => data)
    .catch(async (error) => {
      if (user && error.status === HttpStatusCode.Unauthorized) {
        // Evita logout agresivo en la ventana inmediata post-login:
        // priorizamos refresh/reintento siempre que sea recuperable.
        if (requestsCounter.count > 3 && !isInImmediateAuthWindow()) {
          logoutUserInternal()
          return {}
        }

        // Cuando intentamos refrescar, reiniciamos el contador para no acumular 401s
        // durante carreras de timing.
        requestsCounter.count = 0
        // eslint-disable-next-line no-use-before-define
        return obtainNewAccessToken(url, options)
      }
      return parseJSON(error.response || '', typeRequest)
        .then((data) => Promise.reject(data))
        .catch((message) => {
          if (
            message &&
            message.error &&
            message.error.status === 401 &&
            message.error.reason === 'AuthenticationError'
          ) {
            return Promise.reject(message.error)
          }

          if (message && message.data) {
            return Promise.reject(message)
          }

          if (message && (message.nonFieldErrors || message.detail)) {
            return Promise.reject(message)
          }

          if (message && message.error) {
            return Promise.reject(message)
          }

          const newMessage = ErrorMessage[error.status] || ErrorMessage.Default
          // eslint-disable-next-line prefer-promise-reject-errors
          return Promise.reject({ nonFieldErrors: newMessage })
        })
    })
}

/** Login con usuario/contraseña. El refresh de sesión va por `runRefreshSession` / `exchangeRefreshToken`. */
export function getToken(url: string, body: Record<string, any>): Promise<any> {
  const query = `?app=${loginApp}`
  const fullUrl = String(ApiUrl) + url + query
  const { username } = body
  const password = btoa(body.password)

  const options: any = {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ username, password })
  }

  return request(fullUrl, options)
}

export function getSSOToken(url: string, token: string): Promise<any> {
  const fullUrl = String(ApiUrl) + url
  const options = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `${'Bearer '}${token}`
    }
  }
  return request(fullUrl, options)
}

export function requestForgotPassword(url: string, body: Object): Promise<any> {
  return fetch(url, body).then(checkStatus).then(parseJSON)
}

export function getServers(url: string): Promise<any> {
  const fullUrl = String(import.meta.env.VITE_WORKSPACE_URL) + url
  const user = getCurrentUser()

  const options = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `${'Bearer '}${user ? user.accessToken : ''}`
    }
  }
  return request(fullUrl, options)
}

// noinspection JSUnusedGlobalSymbols
export function verifyToken(url: string): Promise<any> {
  const fullUrl = String(ApiUrl) + url
  const options = {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json'
    }
  }

  return request(fullUrl, options)
}

/**
 * Tras un 401: renueva sesión (single-flight) y reintenta la misma petición con el nuevo access token.
 */
function obtainNewAccessToken(fullUrl: string, options: any): Promise<any> {
  return runRefreshSession()
    .then(() => {
      const u = getCurrentUser()
      if (!u?.accessToken) {
        return {}
      }
      options.headers.Authorization = `Bearer ${u.accessToken}`
      return request(fullUrl, options)
    })
    .catch(() => ({}))
}

// Replace the direct caches reference with a function that checks environment
const getCacheStorage = () => {
  if (typeof caches !== 'undefined') {
    return caches.open(cacheName)
  }
  // Return a mock cache for non-browser environments
  return Promise.resolve({
    match: () => Promise.resolve(null),
    put: () => Promise.resolve(),
    keys: () => Promise.resolve([]),
    delete: () => Promise.resolve(true)
  })
}

const cacheName = 'hub-cache'
const cachePromise = getCacheStorage()

// Creamos una función para obtener una respuesta de la caché
async function getCachedResponse(url: string): Promise<Response | null> {
  const cacheStorage = await cachePromise
  const cachedResponse = await cacheStorage.match(url)

  if (!cachedResponse || !cachedResponse.ok) {
    return null
  }

  return await cachedResponse.json()
}

// Creamos una función para almacenar una respuesta en caché
async function setCacheResponse(url: string, response: Response, typeRequest?: string): Promise<any> {
  try {
    const cache = await cachePromise
    await cache.put(url, new Response(JSON.stringify({ response, typeRequest })))
  } catch (error) {
    return
  }
}

/** No usar filter(async): las Promises son truthy y el resultado del filtrado sería incorrecto. */
async function removeCache(typeRequest: string): Promise<void> {
  const cache = await cachePromise
  const cacheKeys = await cache.keys()
  const keysToDelete: (string | Request)[] = []
  for (const cacheKey of cacheKeys) {
    const cacheResponse = await cache.match(cacheKey)
    if (!cacheResponse || !cacheResponse.ok) continue
    let responseCache: { typeRequest?: string }
    try {
      responseCache = await cacheResponse.json()
    } catch {
      continue
    }
    if (responseCache.typeRequest === typeRequest) {
      keysToDelete.push(cacheKey)
    }
  }
  await Promise.all(keysToDelete.map((key) => cache.delete(key as RequestInfo)))
}

// Creamos una función para intercepta una petición y administrar la caché
async function fetchWithCache(fullUrl: string, options: any, typeRequest: string): Promise<Response> {
  try {
    const saveCache = listHabilityCacheUrl.find((item) => item.type === typeRequest)
    if (saveCache && saveCache.status && options.method === 'GET') {
      const cachedResponse = getCachedResponse(fullUrl)
      const responseCache: any = (await cachedResponse?.then((res) => res)) || null
      if (responseCache && responseCache?.response) return responseCache?.response as any
    }

    if (saveCache && options.method !== 'GET') {
      await removeCache(typeRequest)
    }

    return request(fullUrl, options, typeRequest)
  } catch (error) {
    return request(fullUrl, options, typeRequest)
  }
}

/**
 * Peticiones autenticadas: siempre envían el access token actual. El refresh solo ocurre en `request()`
 * cuando el backend responde 401 (vía `obtainNewAccessToken`). No se usa caducidad calculada en cliente
 * para forzar refresh (la validez la decide el BE).
 */
export function fetchNext(
  type: string,
  url: string,
  body?: Record<string, any>,
  host?: string,
  urlV2: boolean = false,
  typeRequest: string = ''
): Promise<any> {
  let user: User | null = null
  try {
    user = getCurrentUser()
  } catch {
    user = null
  }

  const { idSignalController, ...bodyData } = body || {}
  let fullUrl = ''
  if (host) {
    fullUrl = host + url
  } else {
    fullUrl = ApiUrl ? ApiUrl + url : String(import.meta.env.VITE_WORKSPACE_URL) + url
  }

  if (urlV2) fullUrl = ApiUrlV2 ? ApiUrlV2 + url : String(import.meta.env.VITE_WORKSPACE_URL_V2) + url

  if (type === 'GET' && fullUrl.includes('PE&OLES*')) {
    fullUrl = fullUrl.replace('PE&OLES*', 'PE%26OLES*')
  }

  if (!user?.accessToken) {
    return Promise.reject({ nonFieldErrors: 'No hay sesión activa' })
  }

  let options: any = {
    method: type,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `${'Bearer '}${user.accessToken}`,
      Accept: `application/json;`
    }
  }

  if (idSignalController) {
    const abortCon = (FetchController as any).setAbortController(idSignalController)
    if (abortCon) {
      options.signal = abortCon.signal
    }
  }

  if (bodyData && type !== 'GET') {
    options = { ...options, body: JSON.stringify(bodyData) }
  }

  // Otro request ya disparó refresh por 401: esperar a que rote el access en sesión antes de enviar este.
  if (sessionRefreshInProgress) {
    const initialToken = user.accessToken
    return new Promise((resolve, reject) => {
      const interval = setInterval(() => {
        let u: User | null = null
        try {
          u = getCurrentUser()
        } catch {
          u = null
        }
        if (!u?.accessToken) {
          clearInterval(interval)
          reject({ nonFieldErrors: 'No hay sesión activa' })
          return
        }
        if (u.accessToken !== initialToken) {
          options.headers.Authorization = `${'Bearer '}${u.accessToken}`
          clearInterval(interval)
          resolve(request(fullUrl, options, typeRequest))
        }
      }, PENDING_REQUESTS_DELAY)
      intervals.push(interval)
    })
  }

  requestsCounter.count = 0
  return fetchWithCache(fullUrl, options, typeRequest)
}

/** Renueva sesión en segundo plano (misma lógica que el 401). Si falla, `runRefreshSession` hace logout. */
export const refreshTokenUser = (): void => {
  const user = getCurrentUser()
  if (!user?.refreshToken) {
    logoutUserInternal()
    return
  }
  void runRefreshSession().catch(() => {})
}

/**
 * Refresca el access token usando el refresh token. Retorna Promise<true> si se refrescó bien, Promise<false> si falló (o hace logout).
 * Útil para el chat y otros clientes que necesitan esperar al refresh antes de reintentar la petición.
 */
export const refreshTokenUserAsync = (): Promise<boolean> => {
  const user = getCurrentUser()
  if (!user?.refreshToken) {
    logoutUserInternal()
    return Promise.resolve(false)
  }
  return runRefreshSession()
    .then(() => true)
    .catch(() => false)
}
